// src/hooks/useLiveSensors.js

import { useState, useEffect, useRef, useCallback } from "react";
import { readOfflineCache, writeOfflineCache } from "../utils/offlineCache";

const TEMP_COUNT = 9;
const HUMI_COUNT = 9;
const DIN_COUNT = 68;
const DOUT_COUNT = 72;

const MAX_FAILURES = 5;
const SCAN_INTERVAL_MS = 10000;

const createAnalogSensor = () => ({
  value: "--",
  setpoint: "--",
  isConnect: false,
  triggered: false,
});

const createWindSensor = () => ({
  value: "--",
  setpoint: "--",
  isConnect: false,
  triggered: false,
});

const createDigitalInput = () => ({
  value: "0",
  status: "0",
  isConnect: false,
  triggered: false,
  settings: {
    leadDelay: "",
    lagDelay: "",
    reverse: "0",
    alertSms: "0",
    toDOut: "",
  },
});

const createDigitalOutput = () => ({
  value: "0",
  status: "0",
  isConnect: false,
  triggered: false,
  causedBy: "",
  error: "",
  settings: {
    enable: "0",
    alertSms: "0",
    logicMode: "0",
    timerMode: "0",
    delayOn: "",
    delayOff: "",
    periodicOn: "",
    periodicOff: "",
    dailyOn: "",
    dailyOff: "",
    reverse: "0",
  },
});

const createInitialSensors = () => ({
  temps: Array.from({ length: TEMP_COUNT }, createAnalogSensor),
  humidity: Array.from({ length: HUMI_COUNT }, createAnalogSensor),
  wind: createWindSensor(),
  dIn: Array.from({ length: DIN_COUNT }, createDigitalInput),
  dOut: Array.from({ length: DOUT_COUNT }, createDigitalOutput),
});

const buildDashboardTargets = () => {
  const targets = [];

  for (let i = 0; i < TEMP_COUNT; i++) {
    targets.push(`temp${i}`);
  }

  for (let i = 0; i < HUMI_COUNT; i++) {
    targets.push(`humi${i}`);
  }

  targets.push("wind");

  for (let i = 0; i < DIN_COUNT; i++) {
    targets.push(`dIn${i}`);
  }

  for (let i = 0; i < DOUT_COUNT; i++) {
    targets.push(`dOut${i}`);
  }

  return targets;
};

const isTruthyValue = (value) => {
  return (
    value === true ||
    value === 1 ||
    value === "1" ||
    value === "true" ||
    value === "TRUE" ||
    value === "on" ||
    value === "ON"
  );
};

const normalizeType = (rawType) => {
  if (!rawType) return null;

  const type = String(rawType)
    .trim()
    .replace(/_/g, "")
    .replace(/-/g, "")
    .toLowerCase();

  if (type === "temp" || type === "temperature") return "temp";
  if (type === "humi" || type === "humidity" || type === "humid") return "humi";
  if (type === "wind") return "wind";

  if (
    type === "din" ||
    type === "digitalinput" ||
    type === "digitalin" ||
    type === "input"
  ) {
    return "dIn";
  }

  if (
    type === "dout" ||
    type === "digitaloutput" ||
    type === "digitalout" ||
    type === "output"
  ) {
    return "dOut";
  }

  return null;
};

const extractTypeAndIndex = (data) => {
  let rawType =
    data.type ??
    data.sensorType ??
    data.input_type ??
    data.inputType ??
    data.kind ??
    null;

  let index =
    data.index ?? data.idx ?? data.channel ?? data.number ?? data.no ?? null;

  if (rawType) {
    const match = String(rawType).match(/^([a-zA-Z_]+)(\d+)$/);

    if (match) {
      rawType = match[1];

      if (index === null || index === undefined || index === "") {
        index = Number(match[2]);
      }
    }
  }

  const type = normalizeType(rawType);

  if (type === "wind") {
    index = 0;
  } else if (index !== null && index !== undefined && index !== "") {
    index = Number(index);
  } else {
    index = null;
  }

  return { type, index };
};

const extractRawValue = (rawText, type, index, prop) => {
  if (!rawText || !type || !prop) return "";

  const singleTypes = ["wind", "secu", "gsm", "remote"];
  const safeIndex = Number.isInteger(index) ? index : 0;
  const prefix = singleTypes.includes(type) ? type : `${type}${safeIndex}`;

  const regex = new RegExp(`${prefix}\\.${prop}=([^\\s\\n\\r]+)`, "i");
  const match = rawText.match(regex);

  return match ? match[1] : "";
};

const updateArrayItem = (array, index, patch) => {
  if (!Array.isArray(array)) return array;
  if (!Number.isInteger(index)) return array;
  if (index < 0 || index >= array.length) return array;

  const next = [...array];

  next[index] = {
    ...next[index],
    ...patch,
    settings: patch.settings
      ? {
          ...(next[index]?.settings || {}),
          ...patch.settings,
        }
      : next[index]?.settings,
  };

  return next;
};

const parseRawReportToPatch = (rawText, type, index) => {
  const isDisconnected = rawText.includes("Disconnected");

  const explicitConnect =
    extractRawValue(rawText, type, index, "isConnect") ||
    extractRawValue(rawText, type, index, "isconnect") ||
    extractRawValue(rawText, type, index, "connected");

  const isConnect = isDisconnected
    ? false
    : explicitConnect !== ""
      ? isTruthyValue(explicitConnect)
      : true;

  const trigMatch = rawText.match(/(trig|callTrig|cDTrig):\s*(\d)/i);
  const explicitTrig =
    extractRawValue(rawText, type, index, "trig") ||
    extractRawValue(rawText, type, index, "triggered");

  const triggered =
    explicitTrig !== ""
      ? isTruthyValue(explicitTrig)
      : trigMatch
        ? trigMatch[2] === "1"
        : false;

  const patch = {
    isConnect,
    triggered,
  };

  if (type === "temp" || type === "humi" || type === "wind") {
    let currentValue = "--";

    const valMatch = rawText.match(/(temperature|humidity|wind):\s*([-\d.]+)/i);

    if (valMatch) {
      currentValue = valMatch[2];
    }

    const directValue = extractRawValue(rawText, type, index, "value");
    const setpoint = extractRawValue(rawText, type, index, "setpoint");

    if (directValue !== "") {
      patch.value = directValue;
    } else if (currentValue !== "--") {
      patch.value = currentValue;
    }

    if (setpoint !== "") {
      patch.setpoint = setpoint;
    }

    return patch;
  }

  if (type === "dIn" || type === "dOut") {
    const statusMatch = rawText.match(
      /status:\s*(\d+)(?:\s*\(By:\s*([^)]+)\))?/,
    );
    const statusFromText = statusMatch ? statusMatch[1] : "";

    const directStatus =
      extractRawValue(rawText, type, index, "status") ||
      extractRawValue(rawText, type, index, "value");

    const status = directStatus || statusFromText || "0";

    patch.status = String(status);
    patch.value = String(status);

    if (type === "dOut") {
      const causedBy = statusMatch && statusMatch[2] ? statusMatch[2] : "";

      let errorValue = "";

      if (rawText.includes("_____Error_____")) {
        const errorMatch = rawText.match(/\(Is depended on ([^)]+)\)/);
        errorValue = errorMatch ? errorMatch[1] : "Dependent";
      }

      patch.causedBy = causedBy;
      patch.error = errorValue;

      patch.settings = {
        enable: extractRawValue(rawText, type, index, "enable"),
        alertSms: extractRawValue(rawText, type, index, "alertSms"),
        logicMode: extractRawValue(rawText, type, index, "logicMode"),
        timerMode: extractRawValue(rawText, type, index, "timerMode"),
        delayOn: extractRawValue(rawText, type, index, "delayOn"),
        delayOff: extractRawValue(rawText, type, index, "delayOff"),
        periodicOn: extractRawValue(rawText, type, index, "periodicOn"),
        periodicOff: extractRawValue(rawText, type, index, "periodicOff"),
        dailyOn: extractRawValue(rawText, type, index, "dailyOn"),
        dailyOff: extractRawValue(rawText, type, index, "dailyOff"),
        reverse: extractRawValue(rawText, type, index, "reverse"),
      };
    }

    if (type === "dIn") {
      patch.settings = {
        leadDelay: extractRawValue(rawText, type, index, "leadDelay"),
        lagDelay: extractRawValue(rawText, type, index, "lagDelay"),
        reverse: extractRawValue(rawText, type, index, "reverse"),
        alertSms: extractRawValue(rawText, type, index, "alertSms"),
        toDOut: extractRawValue(rawText, type, index, "toDOut"),
      };
    }

    return patch;
  }

  return patch;
};

const applyRawReport = (prev, rawText, rawType, rawIndex) => {
  const { type, index } = extractTypeAndIndex({
    type: rawType,
    index: rawIndex,
  });

  if (!type) return prev;

  const patch = parseRawReportToPatch(rawText, type, index);

  if (type === "temp") {
    return {
      ...prev,
      temps: updateArrayItem(prev.temps, index, patch),
    };
  }

  if (type === "humi") {
    return {
      ...prev,
      humidity: updateArrayItem(prev.humidity, index, patch),
    };
  }

  if (type === "wind") {
    return {
      ...prev,
      wind: {
        ...prev.wind,
        ...patch,
      },
    };
  }

  if (type === "dIn") {
    return {
      ...prev,
      dIn: updateArrayItem(prev.dIn, index, patch),
    };
  }

  if (type === "dOut") {
    return {
      ...prev,
      dOut: updateArrayItem(prev.dOut, index, patch),
    };
  }

  return prev;
};

const normalizeDirectPatch = (data, type) => {
  const connectValue =
    data.isConnect ?? data.isconnect ?? data.is_connected ?? data.connected;

  const triggerValue = data.triggered ?? data.trig ?? data.trigger;

  const patch = {};

  if (connectValue !== undefined) {
    patch.isConnect = isTruthyValue(connectValue);
  }

  if (triggerValue !== undefined) {
    patch.triggered = isTruthyValue(triggerValue);
  }

  if (type === "temp" || type === "humi" || type === "wind") {
    const value = data.value ?? data.current ?? data.val;
    const setpoint = data.setpoint ?? data.setPoint ?? data.set ?? data.sp;

    if (value !== undefined) {
      patch.value = value;
      if (connectValue === undefined) patch.isConnect = true;
    }

    if (setpoint !== undefined) {
      patch.setpoint = setpoint;
    }

    return patch;
  }

  if (type === "dIn" || type === "dOut") {
    const status = data.status ?? data.value ?? data.state ?? data.val;

    if (status !== undefined) {
      patch.status = String(status);
      patch.value = String(status);
      if (connectValue === undefined) patch.isConnect = true;
    }

    if (type === "dOut") {
      if (data.causedBy !== undefined) patch.causedBy = data.causedBy;
      if (data.error !== undefined) patch.error = data.error;
    }

    return patch;
  }

  return patch;
};

const applyDirectMessage = (prev, data) => {
  const { type, index } = extractTypeAndIndex(data);

  if (!type) return prev;

  const patch = normalizeDirectPatch(data, type);

  if (type === "temp") {
    return {
      ...prev,
      temps: updateArrayItem(prev.temps, index, patch),
    };
  }

  if (type === "humi") {
    return {
      ...prev,
      humidity: updateArrayItem(prev.humidity, index, patch),
    };
  }

  if (type === "wind") {
    return {
      ...prev,
      wind: {
        ...prev.wind,
        ...patch,
      },
    };
  }

  if (type === "dIn") {
    return {
      ...prev,
      dIn: updateArrayItem(prev.dIn, index, patch),
    };
  }

  if (type === "dOut") {
    return {
      ...prev,
      dOut: updateArrayItem(prev.dOut, index, patch),
    };
  }

  return prev;
};

const parsePlainTextMessage = (rawMessage) => {
  if (typeof rawMessage !== "string") return null;

  const text = rawMessage.trim();

  if (!text) return null;

  const dotFormat = text.match(/^([a-zA-Z_]+)(\d+)\.([a-zA-Z_]+)\s*=\s*(.+)$/);

  if (dotFormat) {
    const [, rawType, rawIndex, prop, value] = dotFormat;

    return {
      type: normalizeType(rawType),
      index: Number(rawIndex),
      [prop]: value.trim(),
    };
  }

  const ackFormat = text.match(
    /^ack([a-zA-Z_]+?)(temp|humi|wind|din|dout)(\d*)\s*=\s*(.+)$/i,
  );

  if (ackFormat) {
    const [, rawProp, rawType, rawIndex, value] = ackFormat;

    const propMap = {
      value: "value",
      status: "status",
      isconnect: "isConnect",
      connect: "isConnect",
      trig: "triggered",
      trigger: "triggered",
      triggered: "triggered",
    };

    const normalizedProp = propMap[String(rawProp).toLowerCase()] || rawProp;

    return {
      type: normalizeType(rawType),
      index: rawIndex === "" ? 0 : Number(rawIndex),
      [normalizedProp]: value.trim(),
    };
  }

  return null;
};

export const useLiveSensors = (deviceSerial) => {
  const [sensors, setSensors] = useState(() => {
    const cachedSensors = readOfflineCache("dashboard-live-sensors", deviceSerial);
    return cachedSensors?.data || createInitialSensors();
  });
  const [status, setStatus] = useState("connecting");

  const wsRef = useRef(null);
  const reconnectTimerRef = useRef(null);
  const scanIntervalRef = useRef(null);
  const staleCheckIntervalRef = useRef(null);
  const reconnectAttemptRef = useRef(0);
  const missedScanRef = useRef(0);
  const lastMessageAtRef = useRef(0);
  const isMountedRef = useRef(false);
  const manualCloseRef = useRef(false);
  const connectSocketRef = useRef(null);

  const RECONNECT_BASE_MS = 1000;
  const RECONNECT_MAX_MS = 30000;
  const STALE_CONNECTION_MS = 45000;

  const getReconnectDelay = useCallback((attempt) => {
    const jitter = Math.floor(Math.random() * 700);
    return (
      Math.min(RECONNECT_MAX_MS, RECONNECT_BASE_MS * Math.pow(2, attempt)) +
      jitter
    );
  }, []);

  const getSocketUrl = useCallback(() => {
    const hostname = window.location.hostname;
    const isLocalhost = hostname === "localhost" || hostname === "127.0.0.1";

    if (isLocalhost) {
      return "wss://sfarm.tabangohar.com/ws";
    }

    return `wss://${hostname}/ws`;
  }, []);

  const clearReconnectTimer = useCallback(() => {
    if (reconnectTimerRef.current) {
      clearTimeout(reconnectTimerRef.current);
      reconnectTimerRef.current = null;
    }
  }, []);

  const clearSocketTimers = useCallback(() => {
    if (scanIntervalRef.current) {
      clearInterval(scanIntervalRef.current);
      scanIntervalRef.current = null;
    }

    if (staleCheckIntervalRef.current) {
      clearInterval(staleCheckIntervalRef.current);
      staleCheckIntervalRef.current = null;
    }
  }, []);

  const closeSocket = useCallback(() => {
    clearSocketTimers();

    const ws = wsRef.current;

    if (
      ws &&
      (ws.readyState === WebSocket.OPEN ||
        ws.readyState === WebSocket.CONNECTING)
    ) {
      try {
        ws.close();
      } catch (error) {
        console.error("Live Sensors close socket error:", error);
      }
    }

    wsRef.current = null;
  }, [clearSocketTimers]);

  const sendScanRequest = useCallback(
    (ws = wsRef.current) => {
      if (!ws || ws.readyState !== WebSocket.OPEN || !deviceSerial) return;

      try {
        ws.send(
          JSON.stringify({
            action: "scan",
            serial: deviceSerial,
            targets: buildDashboardTargets(),
          }),
        );

        missedScanRef.current += 1;

        if (missedScanRef.current > 1) {
          setStatus("offline");
        }

        if (missedScanRef.current >= MAX_FAILURES) {
          try {
            ws.close();
          } catch (error) {
            console.error("Live Sensors stale close error:", error);
          }
        }
      } catch (error) {
        console.error("Live Sensors scan send error:", error);
        setStatus("offline");

        try {
          ws.close();
        } catch (closeError) {
          console.error("Live Sensors scan close error:", closeError);
        }
      }
    },
    [deviceSerial],
  );

  const scheduleReconnect = useCallback(
    (immediate = false) => {
      if (!isMountedRef.current || manualCloseRef.current || !deviceSerial)
        return;

      clearReconnectTimer();

      const delay = immediate
        ? 0
        : getReconnectDelay(reconnectAttemptRef.current);

      reconnectTimerRef.current = setTimeout(() => {
        if (!isMountedRef.current || manualCloseRef.current) return;

        reconnectAttemptRef.current += 1;

        if (connectSocketRef.current) {
          connectSocketRef.current();
        }
      }, delay);
    },
    [clearReconnectTimer, deviceSerial, getReconnectDelay],
  );

  const handleSuccessfulMessage = useCallback(
    (updateCallback) => {
      setStatus("online");
      reconnectAttemptRef.current = 0;
      missedScanRef.current = 0;
      lastMessageAtRef.current = Date.now();

      if (typeof updateCallback === "function") {
        setSensors((prev) => {
          const nextSensors = updateCallback(prev);
          writeOfflineCache(
            "dashboard-live-sensors",
            deviceSerial,
            nextSensors,
          );
          return nextSensors;
        });
      }
    },
    [deviceSerial],
  );

  const connectSocket = useCallback(() => {
    if (!deviceSerial || !isMountedRef.current) return;

    if (typeof navigator !== "undefined" && navigator.onLine === false) {
      setStatus("offline");
      scheduleReconnect(false);
      return;
    }

    manualCloseRef.current = false;
    clearReconnectTimer();
    closeSocket();

    setStatus((prev) => (prev === "online" ? prev : "connecting"));

    const wsUrl = getSocketUrl();
    console.log(`🔌 Live Sensors Connecting to: ${wsUrl}`);

    const ws = new WebSocket(wsUrl);
    wsRef.current = ws;
    lastMessageAtRef.current = Date.now();
    missedScanRef.current = 0;

    ws.onopen = () => {
      if (!isMountedRef.current || wsRef.current !== ws) return;

      console.log(`✅ Live Sensors Socket Connected: ${deviceSerial}`);
      setStatus("connecting");
      reconnectAttemptRef.current = 0;
      missedScanRef.current = 0;
      lastMessageAtRef.current = Date.now();

      try {
        ws.send(
          JSON.stringify({
            action: "monitor",
            serial: deviceSerial,
          }),
        );

        sendScanRequest(ws);
      } catch (error) {
        console.error("Live Sensors monitor send error:", error);
        ws.close();
        return;
      }

      clearSocketTimers();

      scanIntervalRef.current = setInterval(() => {
        if (wsRef.current !== ws || ws.readyState !== WebSocket.OPEN) return;
        sendScanRequest(ws);
      }, SCAN_INTERVAL_MS);

      staleCheckIntervalRef.current = setInterval(() => {
        if (wsRef.current !== ws || ws.readyState !== WebSocket.OPEN) return;

        const elapsed = Date.now() - lastMessageAtRef.current;

        if (elapsed > STALE_CONNECTION_MS) {
          console.warn("Live Sensors connection is stale. Reconnecting...");

          try {
            ws.close();
          } catch (error) {
            console.error("Live Sensors stale reconnect error:", error);
          }
        }
      }, 10000);
    };

    ws.onmessage = (event) => {
      if (!isMountedRef.current || wsRef.current !== ws) return;

      let parsed;

      try {
        parsed = JSON.parse(event.data);
      } catch {
        const plainMessage = parsePlainTextMessage(event.data);

        if (!plainMessage) return;

        handleSuccessfulMessage((prev) =>
          applyDirectMessage(prev, plainMessage),
        );
        return;
      }

      handleSuccessfulMessage((prev) => {
        if (Array.isArray(parsed)) {
          return parsed.reduce((current, item) => {
            if (item?.raw) {
              return applyRawReport(current, item.raw, item.type, item.index);
            }

            return applyDirectMessage(current, item);
          }, prev);
        }

        if (parsed?.raw) {
          return applyRawReport(prev, parsed.raw, parsed.type, parsed.index);
        }

        return applyDirectMessage(prev, parsed);
      });
    };

    ws.onerror = () => {
      if (!isMountedRef.current || wsRef.current !== ws) return;

      setStatus("offline");

      try {
        ws.close();
      } catch (error) {
        console.error("Live Sensors socket error close failed:", error);
      }
    };

    ws.onclose = () => {
      if (!isMountedRef.current || wsRef.current !== ws) return;

      clearSocketTimers();
      wsRef.current = null;
      setStatus("offline");
      scheduleReconnect(false);
    };
  }, [
    clearReconnectTimer,
    clearSocketTimers,
    closeSocket,
    deviceSerial,
    getSocketUrl,
    handleSuccessfulMessage,
    scheduleReconnect,
    sendScanRequest,
  ]);

  connectSocketRef.current = connectSocket;

  useEffect(() => {
    if (!deviceSerial) return undefined;

    isMountedRef.current = true;
    manualCloseRef.current = false;

    const cachedSensors = readOfflineCache(
      "dashboard-live-sensors",
      deviceSerial,
    );
    setSensors(cachedSensors?.data || createInitialSensors());
    setStatus(
      typeof navigator !== "undefined" && navigator.onLine === false
        ? "offline"
        : "connecting",
    );
    reconnectAttemptRef.current = 0;
    missedScanRef.current = 0;
    lastMessageAtRef.current = Date.now();

    connectSocket();

    const handleOnline = () => {
      reconnectAttemptRef.current = 0;
      missedScanRef.current = 0;
      setStatus("connecting");
      scheduleReconnect(true);
    };

    const handleOffline = () => {
      setStatus("offline");
      closeSocket();
    };

    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);

    return () => {
      isMountedRef.current = false;
      manualCloseRef.current = true;

      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);

      clearReconnectTimer();
      closeSocket();
    };
  }, [
    clearReconnectTimer,
    closeSocket,
    connectSocket,
    deviceSerial,
    scheduleReconnect,
  ]);

  const manualRetry = useCallback(() => {
    reconnectAttemptRef.current = 0;
    missedScanRef.current = 0;
    manualCloseRef.current = false;

    setStatus("connecting");
    scheduleReconnect(true);
  }, [scheduleReconnect]);

  return {
    sensors,
    status,
    manualRetry,
  };
};
